import { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import { Skeleton } from '@fhs/ui';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import { ICartEntry } from '@rbi-ctg/menu';

import { calculateItemPrice } from './calculate-item-price';
import {
  FreePriceDisplay,
  OfferPriceDisplay,
  OriginalPriceDisplay,
  RewardPriceDisplay,
} from './item-price-components';

interface ItemPriceProps {
  item: ICartEntry;
}

export default function ItemPrice({ item }: ItemPriceProps) {
  const { selectedOffer, selectedOfferSelections, offerEligibleItem } = useAppSelector(state => ({
    selectedOffer: selectors.loyalty.selectSelectedOffer(state),
    selectedOfferSelections: selectors.loyalty.selectSelectedOfferSelections(state),
    offerEligibleItem: selectors.loyalty.selectOfferEligibleItem(state, item.cartId),
  }));

  const offersLoading = useAppSelector(selectors.loyalty.selectOffersLoading);

  const isReward = useMemo(
    () =>
      selectedOffer?.incentives?.some(
        // explicit any because incentiveType is not part of the generated incentive union type
        (incentive: any) => incentive?.incentiveType?.toLowerCase() === 'reward'
      ) ?? false,
    [selectedOffer]
  );

  const hasSelectionForItem = useMemo(
    () => selectedOfferSelections.some(selection => selection.lineId === item.cartId),
    [selectedOfferSelections, item.cartId]
  );

  const isRewardSelectionForItem = hasSelectionForItem && isReward;

  const priceData = useMemo(
    () => calculateItemPrice(item, selectedOffer, offerEligibleItem, isRewardSelectionForItem),
    [item, selectedOffer, offerEligibleItem, isRewardSelectionForItem]
  );

  // Show a skeleton while the backend is evaluating which items are eligible for the discount.
  // This prevents showing incorrect prices (e.g. both BOGO items as "Free") before the
  // backend determines which item actually receives the discount.
  if (hasSelectionForItem && !offerEligibleItem && offersLoading) {
    return <Skeleton style={styles.pricesSkeleton} />;
  }

  const isOfferEligibleItem = !!offerEligibleItem;
  const isRewardWithoutEligibleItem = isRewardSelectionForItem && !isOfferEligibleItem;

  // Strikethrough row shows the undiscounted line price; RewardPriceDisplay / OfferPriceDisplay show finalItemPrice.
  const finalPrice = priceData.shouldShowStrikeThrough
    ? priceData.originalPrice
    : priceData.finalItemPrice;

  const formattedPrice = (finalPrice / 100).toFixed(2);
  const priceLabel =
    priceData.isFree && !isOfferEligibleItem
      ? 'Item is free'
      : priceData.shouldShowStrikeThrough
        ? `Original price ${formattedPrice}, discounted`
        : `Price ${formattedPrice}`;

  return (
    <View
      testID={`item-price-${item.cartId}`}
      style={styles.prices}
      accessibilityRole="text"
      accessibilityLabel={priceLabel}
    >
      <OriginalPriceDisplay
        price={finalPrice}
        shouldShowStrikeThrough={priceData.shouldShowStrikeThrough}
      />

      {/* Reward path: "Free" (no tag) when no modifiers, or modifier price when modifiers remain. */}
      {/* Offer path: tag + "Free" or tag + price on same row only. */}
      <FreePriceDisplay showFree={priceData.isFree && !isOfferEligibleItem} />

      {isRewardWithoutEligibleItem && !priceData.isFree && (
        <RewardPriceDisplay price={priceData.finalItemPrice} />
      )}

      {isOfferEligibleItem && <OfferPriceDisplay price={priceData.finalItemPrice} />}
    </View>
  );
}

const styles = StyleSheet.create({
  prices: {
    alignItems: 'flex-end',
  },
  pricesSkeleton: {
    height: 18,
  },
});
